import React from "react";
import { motion } from "framer-motion";
import NavBar from "./NavBar";
import HeroSection from "./HeroSection";
import ShowcaseSection from "./ShowcaseSection";
import BiofeedbackSection from "./BiofeedbackSection";
import StatsSection from "./StatsSection";
import BenefitsSection from "./BenefitsSection";
import ServicesSection from "./ServicesSection";
import TestimonialsSection from "./TestimonialsSection";
import CTASection from "./CTASection";
import FooterSection from "./FooterSection";
import { Box } from "@mui/material";

/* ── Animated floating ambient orbs ───────────────────────────────── */
const AmbientOrb = ({ color, size, top, left, delay = 0, duration = 20 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      top,
      left,
      background: color,
      filter: `blur(${parseInt(size) * 0.6}px)`,
    }}
    animate={{
      x: [0, 40, -30, 20, 0],
      y: [0, -30, 20, -40, 0],
      scale: [1, 1.15, 0.9, 1.1, 1],
      opacity: [0.4, 0.6, 0.35, 0.55, 0.4],
    }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay,
    }}
  />
);

/* ── Subtle neural network SVG background ─────────────────────────── */
const NeuralBackground = () => {
  const nodes = [
    { x: 5, y: 8 },
    { x: 15, y: 3 },
    { x: 25, y: 12 },
    { x: 35, y: 5 },
    { x: 45, y: 15 },
    { x: 55, y: 7 },
    { x: 65, y: 18 },
    { x: 75, y: 4 },
    { x: 85, y: 13 },
    { x: 95, y: 9 },
    { x: 10, y: 25 },
    { x: 20, y: 30 },
    { x: 30, y: 22 },
    { x: 40, y: 35 },
    { x: 50, y: 28 },
    { x: 60, y: 33 },
    { x: 70, y: 26 },
    { x: 80, y: 38 },
    { x: 90, y: 29 },
    { x: 8, y: 42 },
    { x: 18, y: 48 },
    { x: 28, y: 40 },
    { x: 38, y: 52 },
    { x: 48, y: 45 },
    { x: 58, y: 50 },
    { x: 68, y: 43 },
    { x: 78, y: 55 },
    { x: 88, y: 47 },
    { x: 12, y: 60 },
    { x: 22, y: 65 },
    { x: 32, y: 58 },
    { x: 42, y: 70 },
    { x: 52, y: 63 },
    { x: 62, y: 68 },
    { x: 72, y: 61 },
    { x: 82, y: 73 },
    { x: 92, y: 66 },
    { x: 6, y: 78 },
    { x: 16, y: 83 },
    { x: 26, y: 76 },
    { x: 36, y: 88 },
    { x: 46, y: 81 },
    { x: 56, y: 86 },
    { x: 66, y: 79 },
    { x: 76, y: 91 },
    { x: 86, y: 84 },
    { x: 96, y: 75 },
    { x: 50, y: 95 },
  ];

  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 18) {
        connections.push([i, j]);
      }
    }
  }

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity: 0.035 }}
    >
      {connections.map(([a, b], i) => (
        <motion.line
          key={`c-${i}`}
          x1={nodes[a].x}
          y1={nodes[a].y}
          x2={nodes[b].x}
          y2={nodes[b].y}
          stroke="#8B5CF6"
          strokeWidth="0.15"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{
            duration: 4 + (i % 3),
            repeat: Infinity,
            delay: (i * 0.15) % 4,
          }}
        />
      ))}
      {nodes.map((node, i) => (
        <motion.circle
          key={`n-${i}`}
          cx={node.x}
          cy={node.y}
          r="0.25"
          fill={i % 3 === 0 ? "#8B5CF6" : i % 3 === 1 ? "#C084FC" : "#7393B3"}
          animate={{ opacity: [0.3, 0.9, 0.3], r: [0.2, 0.35, 0.2] }}
          transition={{
            duration: 3 + (i % 4),
            repeat: Infinity,
            delay: (i * 0.2) % 5,
          }}
        />
      ))}
    </svg>
  );
};

const LandingPage = () => {
  return (
    <Box
      className="w-full flex flex-col min-h-screen scroll-smooth relative"
      sx={{
        scrollBehavior: "smooth",
        background:
          "linear-gradient(180deg, #FDFBFE 0%, #F3EAFA 6%, #EDE4F8 14%, #F5EBF6 22%, #F8EAF0 30%, #FDF5F0 38%, #F9F0F7 46%, #EDE4F8 54%, #F5EBF6 62%, #F8EAF0 70%, #FDFBFE 78%, #F3EAFA 86%, #EDE4F8 92%, #0A0F2E 96%, #050816 100%)",
      }}
    >
      {/* Global ambient floating orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <AmbientOrb
          color="rgba(139,92,246,0.12)"
          size="500px"
          top="-5%"
          left="-5%"
          delay={0}
          duration={25}
        />
        <AmbientOrb
          color="rgba(192,132,252,0.10)"
          size="400px"
          top="15%"
          left="70%"
          delay={3}
          duration={22}
        />
        <AmbientOrb
          color="rgba(245,225,236,0.18)"
          size="450px"
          top="30%"
          left="-8%"
          delay={6}
          duration={28}
        />
        <AmbientOrb
          color="rgba(232,213,245,0.14)"
          size="380px"
          top="45%"
          left="60%"
          delay={2}
          duration={20}
        />
        <AmbientOrb
          color="rgba(249,232,217,0.16)"
          size="420px"
          top="55%"
          left="20%"
          delay={8}
          duration={24}
        />
        <AmbientOrb
          color="rgba(139,92,246,0.08)"
          size="350px"
          top="70%"
          left="75%"
          delay={4}
          duration={26}
        />
        <AmbientOrb
          color="rgba(192,132,252,0.10)"
          size="400px"
          top="82%"
          left="-3%"
          delay={10}
          duration={30}
        />
      </div>

      {/* Neural network background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <NeuralBackground />
      </div>

      {/* Content layers */}
      <div className="relative z-10">
        <NavBar />
        {/* Hero + Showcase share the page gradient — bridged by decorations */}
        <div className="relative">
          <HeroSection />

          {/* ── Decorative arcs spanning the boundary ─────────── */}
          <div className="absolute bottom-[-20%] right-[0%] w-[45%] h-[80%] pointer-events-none z-[5]">
            <svg
              className="w-full h-full opacity-[0.08]"
              viewBox="0 0 400 400"
              fill="none"
            >
              {[180, 250, 320].map((r, i) => (
                <motion.circle
                  key={i}
                  cx="400"
                  cy="200"
                  r={r}
                  stroke={["#C084FC", "#8B5CF6", "#7393B3"][i]}
                  strokeWidth="0.7"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 3, delay: 0.5 + i * 0.3 }}
                />
              ))}
            </svg>
          </div>

          {/* ── EEG wave bridging hero → showcase ─────────────── */}
          {/* <div className="absolute bottom-[-2rem] left-0 w-full z-[5] pointer-events-none">
            <svg
              className="w-full h-20"
              viewBox="0 0 1000 60"
              preserveAspectRatio="none"
            >
              <motion.path
                d="M0,30 Q50,30 80,18 T140,42 T200,14 T260,46 T320,24 T380,36 T440,12 T500,48 T560,20 T620,40 T680,18 T740,34 T800,26 T860,42 T920,14 T980,36 T1040,24 T1100,46 T1160,18 T1220,34 T1280,26 T1340,36 T1400,30"
                fill="none"
                stroke="url(#bridgeEEG)"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 4, ease: "easeOut" }}
                style={{ opacity: 0.25 }}
              />
              <motion.path
                d="M0,35 Q60,35 90,22 T150,45 T220,18 T290,42 T360,28 T430,38 T500,15 T570,44 T640,22 T710,40 T780,20 T850,38 T920,24 T990,42 T1060,16 T1130,40 T1200,22 T1270,36 T1340,28 T1400,35"
                fill="none"
                stroke="url(#bridgeEEG2)"
                strokeWidth="1.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 4.5, ease: "easeOut", delay: 0.5 }}
                style={{ opacity: 0.15 }}
              />
              <defs>
                <linearGradient
                  id="bridgeEEG"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#7393B3" stopOpacity="0" />
                  <stop offset="20%" stopColor="#8B5CF6" />
                  <stop offset="50%" stopColor="#C084FC" />
                  <stop offset="80%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#7393B3" stopOpacity="0" />
                </linearGradient>
                <linearGradient
                  id="bridgeEEG2"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#C084FC" stopOpacity="0" />
                  <stop offset="30%" stopColor="#EC4899" stopOpacity="0.6" />
                  <stop offset="70%" stopColor="#8B5CF6" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#7393B3" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div> */}

          <ShowcaseSection />
        </div>
        <BiofeedbackSection />
        <StatsSection />
        <BenefitsSection />
        <ServicesSection />
        <TestimonialsSection />
        <CTASection />
        <FooterSection />
      </div>
    </Box>
  );
};

export default LandingPage;
